/**
 * Lookup Hooks — Supabase Direct (Phase 2E)
 *
 * Replaces: components/services/sharedAPI/useLookup.ts + useLookups.ts + useGetSubjectsApi.ts
 * New path:  Supabase client → PostgreSQL (direct, RLS-secured)
 *
 * Return API is preserved exactly from the old useLookup pattern so no consumer
 * changes are needed: useLookup("grade_levels") → { gradeLevels, getGradeLevels, loading }
 */
import { useCallback, useMemo, useState } from 'react';
import { camelCase } from 'lodash';
import { isCancelledError, useQuery } from '@tanstack/react-query';
import { supabase, type Tables } from '../supabase';
import { reportError } from '../reportError';
import { queryClient } from '../queryClient';
import { CLASSES_STALE_MS, LOOKUP_STALE_MS, lookupQueryKey } from '../query/lookupCache';
import { fetchAllRows } from './fetchAllRows';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LookupType =
  | 'school_years'
  | 'skills'
  | 'sub_skills'
  | 'programs'
  | 'grade_levels'
  | 'curriculums'
  | 'subjects'
  | 'accreditations'
  | 'organizations'
  | 'grade_approval_status'
  | 'students'
  | 'classes'
  | 'students_by_class'
  | 'activity-type'
  | 'evaluation_type'
  | 'grading_component'
  | 'objective_type'
  | 'teachers'
  | 'parent_teachers'
  | 'catalogs'
  | 'course-status';

type CamelCase<S extends string> = S extends `${infer Left}-${infer Right}`
  ? `${Lowercase<Left>}${Capitalize<CamelCase<Right>>}`
  : S extends `${infer Left}_${infer Right}`
    ? `${Lowercase<Left>}${Capitalize<CamelCase<Right>>}`
    : Lowercase<S>;

type UseLookupReturn<T extends LookupType, R> = {
  [K in T as CamelCase<`get-${K}`>]: (options?: any) => Promise<void>;
} & {
  [K in T as CamelCase<K>]: R;
} & {
  loading: boolean;
};

// ---------------------------------------------------------------------------
// Static data for types with no DB table
// ---------------------------------------------------------------------------

const STATIC_LOOKUPS: Partial<Record<LookupType, any[]>> = {
  grade_approval_status: [
    { id: 'open', name: 'Open' },
    { id: 'advisor-approval', name: 'For Advisor Approval' },
    { id: 'registrar-approval', name: 'For Registrar Approval' },
    { id: 'locked', name: 'Locked In' },
    { id: 'rejected', name: 'Rejected' },
  ],
  organizations: [], // Served by RLS — org context resolves via Supabase session
  sub_skills: [],    // Mapped to competencies — Phase 3 will connect
  'activity-type': [
    { id: 'task', name: 'Task' },
    { id: 'video', name: 'Video' },
    { id: 'audio', name: 'Audio' },
    { id: 'assessment', name: 'Assessment' },
  ],
  evaluation_type: [
    { id: 'objective', name: 'Objective' },
    { id: 'subjective', name: 'Subjective' },
  ],
  objective_type: [
    { id: 'knowledge', name: 'Knowledge' },
    { id: 'comprehension', name: 'Comprehension' },
    { id: 'application', name: 'Application' },
    { id: 'analysis', name: 'Analysis' },
    { id: 'synthesis', name: 'Synthesis' },
    { id: 'evaluation', name: 'Evaluation' },
  ],
  'course-status': [
    { id: 'draft', name: 'Draft' },
    { id: 'for-approval', name: 'For Approval' },
    { id: 'approved', name: 'Approved' },
    { id: 'rejected', name: 'Rejected' },
    { id: 'archived', name: 'Archived' },
  ],
};

// ---------------------------------------------------------------------------
// Supabase fetch strategy per lookup type
// ---------------------------------------------------------------------------

const rowsOrThrow = <R>(result: { data: R[] | null; error: unknown }): R[] => {
  if (result.error) throw result.error;

  return result.data ?? [];
};

const UNCACHED_LOOKUPS = new Set<LookupType>(['students', 'students_by_class']);

const NO_ROWS: any[] = [];

const fetchByType = async (type: LookupType, params?: any): Promise<any[]> => {
  const staticData = STATIC_LOOKUPS[type];
  if (staticData !== undefined) return staticData;

  switch (type) {
    case 'grade_levels':
      return rowsOrThrow(await supabase.from('grade_levels').select('*').order('grade_level'));

    case 'school_years':
      // Embed grading_periods aliased as `periods` so the registrar/PT GradeBooks
      // period-column headers (schoolYears.find(...).periods, read for
      // `.period_name`) come from the canonical grading_periods spine. The dead
      // school_years.periods JSONB column (always []) is dropped.
      return rowsOrThrow(
        await supabase
          .from('school_years')
          .select('*, periods:grading_periods(id, period_name, start_date, end_date)')
          .order('name'),
      );

    case 'curriculums':
      return rowsOrThrow(await supabase.from('curriculums').select('*').order('curriculum_name'));

    case 'programs':
      return rowsOrThrow(await supabase.from('programs').select('*').order('program'));

    case 'subjects':
      return rowsOrThrow(await supabase.from('subjects').select('*').order('name'));

    case 'accreditations':
      // accreditations table has `accreditation`, not `name`. Map it up to
      // a synthetic `name` field so existing dropdown consumers that read
      // `option.name` keep working.
      return rowsOrThrow(await supabase.from('accreditations').select('*').order('accreditation'))
        .map((r: any) => ({ ...r, name: r.accreditation }));

    case 'skills':
      // skills table has `title`, not `name`.
      return rowsOrThrow(await supabase.from('skills').select('*').order('title'))
        .map((r: any) => ({ ...r, name: r.title }));

    case 'grading_component':
      // grading_components has `component`, not `name`.
      return rowsOrThrow(await supabase.from('grading_components').select('*').order('component'))
        .map((r: any) => ({ ...r, name: r.component }));

    case 'classes': {
      const { data, error } = await fetchAllRows<Tables<'classes'>>((from, to) =>
        supabase.from('classes').select('*').order('class_name').order('id').range(from, to),
      );

      if (error) throw error;

      return data;
    }

    case 'catalogs':
      return rowsOrThrow(await supabase.from('catalogs').select('*').order('catalog_name'));

    case 'students': {
      // students has THREE FKs to users (created_by, updated_by, user_id),
      // so PostgREST refuses to embed `users(...)` without a hint —
      // returns 300 with a "more than one relationship" error. Force the
      // intended FK explicitly.
      const { data, error } = await fetchAllRows<{
        id: string;
        id_number: string | null;
        user_id: string | null;
        users: { first_name: string | null; last_name: string | null; email: string | null } | null;
      }>((from, to) =>
        supabase
          .from('students')
          .select('id, id_number, user_id, users!students_user_id_fkey(first_name, last_name, email)')
          .order('id_number')
          .order('id')
          .range(from, to),
      );

      if (error) throw error;

      return data.map((row) => ({
        id: row.id,
        id_number: row.id_number,
        user_id: row.user_id,
        first_name: row.users?.first_name ?? '',
        last_name: row.users?.last_name ?? '',
        email: row.users?.email ?? '',
      }));
    }

    case 'teachers':
      return rowsOrThrow(
        await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .eq('base_role', 'teacher')
          .order('last_name'),
      );

    case 'parent_teachers': {
      const { data, error } = await fetchAllRows<Pick<Tables<'users'>, 'id' | 'first_name' | 'last_name' | 'email'>>((from, to) =>
        supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .eq('base_role', 'parent-teacher')
          .order('last_name')
          .order('id')
          .range(from, to),
      );

      if (error) throw error;

      return data;
    }

    case 'students_by_class': {
      const classId = params?.class_id;
      let query = supabase
        .from('class_students')
        .select('*, students(id, first_name, last_name, email)');
      if (classId) query = query.eq('class_id', classId);

      return rowsOrThrow(await query);
    }

    default:
      return [];
  }
};

// ---------------------------------------------------------------------------
// useLookup — Generic Supabase lookup (exact same return API as old hook)
// ---------------------------------------------------------------------------

export const useLookup = <T extends LookupType>(
  type: T,
  params: any = {},
  useCache: boolean = true,
): UseLookupReturn<T, any[]> => {
  const [requested, setRequested] = useState(false);
  const cachedFor = type === 'classes' ? CLASSES_STALE_MS : LOOKUP_STALE_MS;
  const staleTime = useCache && !UNCACHED_LOOKUPS.has(type) ? cachedFor : 0;

  const lookupOptions = {
    queryKey: lookupQueryKey(type, params),
    queryFn: () => fetchByType(type, params),
    staleTime,
    retry: false,
  };

  const query = useQuery({ ...lookupOptions, enabled: requested }, queryClient);

  const fetchData = useCallback(async (_options?: any) => {
    setRequested(true);
    try {
      await queryClient.fetchQuery(lookupOptions);
    } catch (err) {
      if (isCancelledError(err)) return;

      reportError({ op: `Load lookup ${type}`, err, silent: true, tags: { lookup: String(type), verb: 'lookup' } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return {
    [camelCase(`get-${type}`)]: fetchData,
    [camelCase(type)]: requested ? (query.data ?? NO_ROWS) : NO_ROWS,
    loading: requested && query.isFetching,
  } as UseLookupReturn<T, any[]>;
};

// ---------------------------------------------------------------------------
// useGetSubjectsLookupApi — Subjects with optional grade_level / curriculum filter
// ---------------------------------------------------------------------------

export interface GetSubjectsLookupParam {
  grade_level_id?: string;
  curriculum_id?: string;
}

export const useGetSubjectsLookupApi = (_defaultParams?: GetSubjectsLookupParam) => {
  const [rawSubjects, setRawSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const getSubjects = useCallback(async (params?: GetSubjectsLookupParam) => {
    setLoading(true);
    try {
      const gradeId = params?.grade_level_id;
      const curriculumId = params?.curriculum_id;
      // A subject links to a grade via the subject_grades junction OR the
      // all_grades shortcut, and to a curriculum via the curriculum_subjects
      // junction. The legacy direct subjects.grade_level_id /
      // subjects.curriculum_id columns were dropped (schema cleanup R1b — both
      // always NULL), so both filters resolve through their junction tables.
      let junctionIds: string[] = [];
      if (gradeId) {
        const { data: sgRows } = await supabase
          .from('subject_grades')
          .select('subject_id')
          .eq('grade_level_id', gradeId);
        junctionIds = ((sgRows ?? []) as Array<{ subject_id: string }>).map((r) => r.subject_id);
      }

      // Curriculum → subjects via the curriculum_subjects junction.
      let curriculumSubjectIds: string[] | null = null;
      if (curriculumId) {
        const { data: csRows } = await supabase
          .from('curriculum_subjects')
          .select('subject_id')
          .eq('curriculum_id', curriculumId);
        curriculumSubjectIds = ((csRows ?? []) as Array<{ subject_id: string }>).map((r) => r.subject_id);
        // No subjects linked to this curriculum → no possible matches.
        if (curriculumSubjectIds.length === 0) {
          setRawSubjects([]);
          return;
        }
      }

      let query = supabase.from('subjects').select('*');
      if (gradeId) {
        const clauses = [`all_grades.eq.true`];
        if (junctionIds.length > 0) {
          clauses.push(`id.in.(${junctionIds.join(',')})`);
        }
        query = (query as any).or(clauses.join(','));
      }
      if (curriculumSubjectIds) {
        query = (query as any).in('id', curriculumSubjectIds);
      }
      const { data } = await (query as any).order('name');
      setRawSubjects(data ?? []);
    } catch (err) {
      reportError({ op: 'Load subjects lookup', err, silent: true, tags: { lookup: 'subjects', verb: 'lookup' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const subjects = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return [...rawSubjects].sort((a, b) => collator.compare(a.name ?? '', b.name ?? ''));
  }, [rawSubjects]);

  return { loadingSubjects: loading, subjects, getSubjects };
};

// ---------------------------------------------------------------------------
// getSubjectsForCurricula — subject UNION across many curricula (contract C1)
// ---------------------------------------------------------------------------

/** The subject lookup row shape (subjects table: id, name, color, image, …). */
export type SubjectRow = Tables<'subjects'>;

/**
 * UNION of the subjects linked to ANY curriculum in `curriculumIds` (via the
 * `curriculum_subjects` junction), filtered to `gradeLevelId` (a subject links
 * to a grade via the `subject_grades` junction OR the `subjects.all_grades`
 * shortcut), DEDUPED by subject id.
 *
 * Same junction-resolution approach as `useGetSubjectsLookupApi`, generalised
 * from a single curriculum to the course's full approved-for set: the legacy
 * direct `subjects.curriculum_id` / `subjects.grade_level_id` columns were
 * dropped (schema cleanup R1b — always NULL), so both filters resolve through
 * their junction tables.
 *
 * Returns `[]` when `curriculumIds` is empty (no curriculum → no eligibility).
 * Passing `gradeLevelId = null` skips the grade filter and returns the whole
 * curriculum union.
 */
export const getSubjectsForCurricula = async (
  curriculumIds: string[],
  gradeLevelId: string | null,
): Promise<SubjectRow[]> => {
  try {
    const curriculumIdSet = [...new Set((curriculumIds ?? []).filter(Boolean))];
    if (curriculumIdSet.length === 0) return [];

    // Curriculum → subjects via the curriculum_subjects junction (UNION across
    // every curriculum in the set, deduped).
    const { data: csRows } = await supabase
      .from('curriculum_subjects')
      .select('subject_id')
      .in('curriculum_id', curriculumIdSet);
    const curriculumSubjectIds = [
      ...new Set(
        ((csRows ?? []) as Array<{ subject_id: string }>).map((r) => r.subject_id),
      ),
    ];
    // No subjects linked to any of these curricula → no possible matches.
    if (curriculumSubjectIds.length === 0) return [];

    // Grade → subjects via the subject_grades junction (the all_grades shortcut
    // is applied below, on the subjects query itself).
    let gradeJunctionIds: string[] = [];
    if (gradeLevelId) {
      const { data: sgRows } = await supabase
        .from('subject_grades')
        .select('subject_id')
        .eq('grade_level_id', gradeLevelId);
      gradeJunctionIds = ((sgRows ?? []) as Array<{ subject_id: string }>).map(
        (r) => r.subject_id,
      );
    }

    const baseQuery = supabase.from('subjects').select('*').in('id', curriculumSubjectIds);
    // Apply the grade filter (all_grades shortcut OR junction-matched ids) as a
    // single chained expression so the typed PostgrestFilterBuilder is preserved
    // (no `any` cast on a reassigned `let`).
    const filteredQuery = gradeLevelId
      ? baseQuery.or(
          [
            `all_grades.eq.true`,
            ...(gradeJunctionIds.length > 0 ? [`id.in.(${gradeJunctionIds.join(',')})`] : []),
          ].join(','),
        )
      : baseQuery;
    const { data } = await filteredQuery.order('name');

    // Dedupe by subject id (a subject linked to >1 curriculum in the set would
    // otherwise appear once per curriculum). `in('id', …)` already collapses
    // that, but guard explicitly so the union contract holds regardless.
    const seen = new Set<string>();
    const deduped: SubjectRow[] = [];
    for (const row of (data ?? []) as SubjectRow[]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      deduped.push(row);
    }
    return deduped;
  } catch (err) {
    reportError({
      op: 'Load subjects for curricula',
      err,
      silent: true,
      tags: { lookup: 'subjects', verb: 'lookup' },
    });
    return [];
  }
};

// ---------------------------------------------------------------------------
// useGetGradingPeriodLookupApi — Grading periods (sorted by start_date)
// ---------------------------------------------------------------------------

export const useGetGradingPeriodLookupApi = (_defaultParams?: { topic_id?: string }) => {
  const [rawPeriods, setRawPeriods] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const getPeriods = useCallback(async (_topic_id?: string) => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('grading_periods')
        .select('*')
        .order('start_date');
      setRawPeriods(data ?? []);
    } catch (err) {
      reportError({ op: 'Load grading periods lookup', err, silent: true, tags: { lookup: 'grading_periods', verb: 'lookup' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const periods = useMemo(
    () => [...rawPeriods].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()),
    [rawPeriods],
  );

  return { loadingPeriods: loading, periods, getPeriods };
};

// ---------------------------------------------------------------------------
// useGetCoursesLookupApi — All courses sorted by title
// ---------------------------------------------------------------------------

export const useGetCoursesLookupApi = (_defaultParams?: Record<string, never>) => {
  const [rawCourses, setRawCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const getCourses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('courses')
        .select('id, course_title, status, subjects(name), grade_levels(grade_level)')
        .order('course_title');
      setRawCourses(data ?? []);
    } catch (err) {
      reportError({ op: 'Load courses lookup', err, silent: true, tags: { lookup: 'courses', verb: 'lookup' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const courses = useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    return [...rawCourses].sort((a, b) => collator.compare(a.course_title ?? '', b.course_title ?? ''));
  }, [rawCourses]);

  return { loadingCourses: loading, courses, getCourses };
};

// ---------------------------------------------------------------------------
// useGetPeriodTitlesLookupApi — Distinct period names from grading_periods
// ---------------------------------------------------------------------------

export const useGetPeriodTitlesLookupApi = (_defaultParams?: Record<string, never>) => {
  const [rawTitles, setRawTitles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const getPeriodTitles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('grading_periods')
        .select('period_name')
        .order('period_name');
      const titles = [...new Set((data ?? []).map((p: any) => p.period_name))];
      setRawTitles(titles);
    } catch (err) {
      reportError({ op: 'Load period titles lookup', err, silent: true, tags: { lookup: 'period_titles', verb: 'lookup' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const periodTitles = useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    return [...rawTitles].sort((a, b) => collator.compare(a, b));
  }, [rawTitles]);

  return { loadingPerioTitles: loading, periodTitles, getPeriodTitles };
};

// ---------------------------------------------------------------------------
// useGetStudentsLookupApi — Students (id, first_name, last_name, email)
// ---------------------------------------------------------------------------

export interface GetStudentsLookupParam {
  active?: boolean;
}

export interface StudentLookupModel {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export const useGetStudentsLookupApi = (_defaultParams?: GetStudentsLookupParam) => {
  const [studentLookups, setStudentLookups] = useState<StudentLookupModel[]>([]);
  const [loading, setLoading] = useState(false);

  const getStudentLookups = useCallback(async (_params?: GetStudentsLookupParam) => {
    setLoading(true);
    try {
      let query = supabase
        .from('users')
        .select('id, first_name, last_name, email')
        .eq('base_role', 'student');
      const { data } = await (query as any).order('last_name');
      setStudentLookups(data ?? []);
    } catch (err) {
      reportError({ op: 'Load students lookup', err, silent: true, tags: { lookup: 'students', verb: 'lookup' } });
    } finally {
      setLoading(false);
    }
  }, []);

  return { loadingStudentLookups: loading, studentLookups, getStudentLookups };
};

// ---------------------------------------------------------------------------
// useGetSubjectsApi — Subjects list (same return shape as old hook)
// ---------------------------------------------------------------------------

export const useGetSubjectsApi = (_defaultParams: Record<string, never>) => {
  const [subjects, setSubjects] = useState<{ subject: any[] } | null>(null);
  const [loading, setLoading] = useState(false);

  const getSubjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase.from('subjects').select('*').order('name');
      setSubjects({ subject: data ?? [] });
    } catch (err) {
      reportError({ op: 'Load subjects list', err, silent: true, tags: { lookup: 'subjects', verb: 'lookup' } });
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, subjects, getSubjects };
};
